/**
 * FASE 4.2.3 — useClientProfilePrefill (REFACTOR)
 *
 * Hook per i wizard pubblici che pre-compila anagrafica/residenza/contatti
 * dell'utente loggato.
 *
 * IMPORTANTE: l'endpoint `/api/client/me` NON ritorna i PII (sono nel blob
 * cifrato `_enc` filtrato dalla projection). Per ottenere i dati decifrati
 * usiamo `POST /api/client/quote-prefill` che decifra `_enc` lato server e
 * restituisce `prefilled.contraente`.
 *
 * Usage:
 *   const prefill = useClientProfilePrefill('casa');
 *   if (prefill.loggedIn && prefill.loaded) { ... }
 *
 * Ritorna:
 *   { loggedIn, loaded, loading, anagrafica, residenza, contatti,
 *     customerName, raw }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useClientAuth } from "@/contexts/ClientAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;

export default function useClientProfilePrefill(prodotto = "casa") {
  const { account, getToken } = useClientAuth();
  const [data, setData] = useState(null);  // { anagrafica, residenza, contatti }
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!account) {
      setData(null); setLoaded(false);
      return;
    }
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setLoaded(false);

    axios.post(
      `${API}/quote-prefill`,
      { prodotto, profile_ids: {} },
      { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
    )
      .then((r) => {
        const body = r?.data || {};
        // Backend ritorna `prefilled.contraente` (e alias `prefilled_by_role`)
        const contraente =
          body?.prefilled?.contraente ||
          body?.prefilled_by_role?.contraente ||
          {};
        const anagrafica = {
          nome: contraente.nome || "",
          cognome: contraente.cognome || "",
          codice_fiscale: contraente.cf || contraente.codice_fiscale || "",
          data_nascita: contraente.data_nascita || "",
          luogo_nascita: contraente.luogo_nascita || "",
          sesso: contraente.sesso || "",
          stato_civile: contraente.stato_civile || "",
          titolo_studio: contraente.titolo_studio || "",
          professione: contraente.professione_label || contraente.professione || "",
        };
        const residenza = contraente.residenza
          ? {
              via: contraente.residenza.via || "",
              civico: contraente.residenza.civico || "",
              cap: contraente.residenza.cap || "",
              citta: contraente.residenza.citta || "",
              provincia: contraente.residenza.provincia || "",
              nazione: contraente.residenza.nazione || "IT",
            }
          : null;
        const contatti = {
          email: contraente.email || account?.email || "",
          telefono: contraente.telefono || account?.phone_e164 || "",
          otp_verified: true,
        };
        setData({ anagrafica, residenza, contatti, raw: body });
        setLoaded(true);
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV !== "production") {
          console.debug("[useClientProfilePrefill] loaded", { prodotto, anagrafica, residenza, contatti });
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[useClientProfilePrefill] fetch failed", err?.response?.status, err?.message);
        setData(null);
        setLoaded(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, prodotto]);

  const anagrafica = data?.anagrafica || null;
  const residenza = data?.residenza || null;
  const contatti = data?.contatti || null;

  // customerName: priorità (1) nome decifrato dal profilo,
  //   (2) account.nome_completo se disponibile, (3) stringa vuota
  //   (il banner mostra fallback generico se vuota). MAI email local-part.
  const customerName =
    (anagrafica?.nome || "").trim() ||
    (account?.nome_completo || "").trim() ||
    "";

  return {
    loggedIn: !!account,
    loaded,
    loading,
    profile: data?.raw || null,
    anagrafica,
    residenza,
    contatti,
    customerName,
  };
}
